import TileButton from 'components/atoms/TileButton';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Legend from 'components/molecules/Legend';
import { commonClassName } from 'components/pages/PromoScorecard';
import {
    bumpChartHeight,
    legendWidth,
    multiSelectionDropdownLabel,
    sliderWidth,
    TileData,
    xAxisLabel,
} from 'components/template/BumpReport/MainSection';
import { bumpReportUrls } from 'config/urls';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useFetchBumpReportGraphRQ } from 'hooks/bumpReport/useFetchBumpReportGraphRQ';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    fetchCurrency,
    fetchUnit,
    formatUnit,
    getConfig,
    parseSliderValue,
    resolveBumpReportOptionSelection,
    subtractYear,
} from 'utils/helperMethods';
import BumpChartContainer from './BumpChartContainer';
import Loader from 'components/molecules/Loader';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { yAxisLabelClass } from 'components/template/GrowthAnalysis/ExpandedView';
import dayjs from 'dayjs';
import _, { first } from 'lodash';
import BumpChartComparisonContainer from './BumpChartComparisonContainer';
import SelectionDropDown from 'components/molecules/SelectionDropDown';
import { CustomSlider } from 'components/pages/RangeSlider';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { useFetchGraphExportRQ } from 'hooks/bumpReport/useFetchGraphExportRQ';
import { COMPARISON_CHART_CONTAINER_IDS, frequencyDaysMapping, sliderMarksConfig } from 'config/constants';
import { useFetchGraphExportComparison } from 'hooks/bumpReport/useFetchGraphExportComparison';
import GrowthAnalysisTable from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import { bumpReportChartParser } from 'parser/bumpReportChartParser';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { BumpReportFromType } from 'types/common';

type Props = {
    isMedia: boolean;
    tileData: TileData;
    bumpReportTile1DataList: any[];
    selectedBumpReportTile1Data: any;
    selectedBumpReportTile2Data: any;
    groupByOptionSelected: any;
    attributeTypeSelected: any;
    showAllLabels: boolean;
    from: BumpReportFromType;
    frequencyDropdownSelectedItem: Record<string, any>;
};
const promoComparisonSubHeader = 'Comparison';

const comparisonTableTileData = [
    { label: 'CY', key: 'cy' },
    { label: 'YA', key: 'ya' },
    { label: 'Difference', key: 'difference' },
];

const PromoComparisonContainerV2: FC<Props> = ({
    showAllLabels,
    isMedia,
    tileData,
    bumpReportTile1DataList,
    groupByOptionSelected,
    selectedBumpReportTile1Data,
    selectedBumpReportTile2Data,
    attributeTypeSelected,
    from = 'bump-report',
    frequencyDropdownSelectedItem,
}) => {
    const { appConfig } = useContext(UserContext);
    const isShipmentBumpReport = from === 'shipment-bump-report';
    const bumpReportConfig = getConfig(isShipmentBumpReport ? 'shipmentBumpReport' : 'bumpReport', appConfig);
    const {
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodLoading,
        timeSelectionLabel,
        allCheckedAttribute,
        timePeriodSelection,
        idForRestOfComponentLoading,
        currentFrequency,
        screenFilterConfig,
    } = useContext(FilterContextData);
    //if frequencyDropdownSelectedItem is not present, use currentFrequency
    const frequencyToConsider = frequencyDropdownSelectedItem ?? currentFrequency;

    const sliderConfig = sliderMarksConfig?.[frequencyToConsider?.key ?? 'W'];
    const sliderConfigKeys = Object.keys(sliderConfig);
    const sliderMin = sliderConfigKeys?.[0];
    const sliderMax = sliderConfigKeys?.[sliderConfigKeys?.length - 1];
    const groupByKey = bumpReportConfig.groupByKey;
    const bumpChartY2AxisOptions = bumpReportConfig.bumpChartY2AxisOptions?.[tileData?.volTile.key] || [];
    const timePeriod = timePeriodSelection && JSON.parse(timePeriodSelection);

    const refForGraph = useRef<any>();

    const valAbsUnitMapping: any = {
        val_abs: ['Sales', fetchCurrency(appConfig)],
        vol_abs: ['Sales', `(${fetchUnit(appConfig, 'volume')})`],
        val_percentage: ['Sales', '(%)'],
        vol_percentage: ['Volume Sales', '(%)'],
        units_abs: ['Sales', '(Units)'],
        units_percentage: ['Unit Sales', '(%)'],
    };
    const mapAbsVolTile = (tileData: any) => {
        return valAbsUnitMapping[`${tileData.volTile.key}_${tileData.absTile.key}`];
    };

    const incrementalPromoVariables = {
        time_period: timePeriod,
        filters: idForRestOfComponent,
        data_type: tileData.bumpReportTile.key === 'increments' ? 'base_incremental' : 'promo',
        metric_type: tileData.volTile.dataKey,
        investment_categories: bumpReportTile1DataList?.map((item: any) => item?.key),
        investment_type: selectedBumpReportTile1Data?.key,
        data_level: selectedBumpReportTile2Data?.key,
        isComparison: false,
        frequency: frequencyToConsider?.key,
        featureName: screenFilterConfig?.dimensionListKey ?? 'default',
        is_custom_date: false,
        is_year_ago: false,
    };

    const yaIncrementalPromoVariables = {
        ...incrementalPromoVariables,
        // time_period: yaTimePeriod,
        is_year_ago: true,
    };

    const incrementalPromoExportVariables = {
        ...incrementalPromoVariables,
        ya_time_period: incrementalPromoVariables?.time_period,
        kpi_list: bumpChartY2AxisOptions?.map((item: any) => item?.value) || [],
    };

    const comparisonTileData = bumpReportConfig?.comparisonTileData;
    const [selectedComparisonTile, setSelectedComparisonTile] = useState(comparisonTileData?.[0]);
    const [selectedTableComparisonTile, setSelectedTableComparisonTile] = useState(comparisonTableTileData?.[0]);

    const [optionSelected, setOptionSelected] = useState([bumpChartY2AxisOptions[0]?.value]);
    const [sliderValue, setSliderValue] = useState(parseSliderValue(sliderConfigKeys?.[1]));
    const [chartConfig, setChartConfig] = useState({
        barWidth: 20,
        width: 1093,
    });

    // When volTile changes, preserve valid selections or fall back to default
    useEffect(() => {
        setOptionSelected((prev) => resolveBumpReportOptionSelection(bumpChartY2AxisOptions, prev));
    }, [tileData.volTile.key]);

    useEffect(() => {
        setSliderValue(parseSliderValue(sliderConfigKeys?.[1]));
    }, [frequencyToConsider]);

    const groupByVariables = {
        time_period: timePeriod,
        isComparison: false,
        filters: idForRestOfComponent,
        data_type:
            groupByOptionSelected?.index === 0
                ? 'base_incremental'
                : groupByOptionSelected?.index === 1
                ? 'promo'
                : 'group_by',
        metric: groupByOptionSelected?.value,

        dimension: attributeTypeSelected?.dimension || '',
        attribute_type: _.snakeCase(attributeTypeSelected?.label) || '',
        frequency: frequencyToConsider?.key,
        featureName: screenFilterConfig?.dimensionListKey ?? 'default',
        is_year_ago: false,
        is_custom_date: false,
    };
    const groupByExportVariables = {
        ...groupByVariables,
        ya_time_period: incrementalPromoVariables?.time_period,
    };
    const yaGroupByVariables = {
        ...groupByVariables,
        is_year_ago: true,
        // time_period: yaTimePeriod,
    };

    //react queries
    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            idForRestOfComponent,
    );

    const isGroupBy = tileData.bumpReportTile.key === groupByKey;

    const selectedOptionsWithLabels = useMemo(
        () => bumpChartY2AxisOptions.filter((item: any) => optionSelected.includes(item.value)),
        [optionSelected],
    );

    const legendLabels = useMemo(() => {
        const stringifiedOptions = Array.isArray(selectedOptionsWithLabels)
            ? selectedOptionsWithLabels?.map((item) => item?.label)?.toString()
            : [];
        return stringifiedOptions || '';
    }, [selectedOptionsWithLabels]);

    const isDifferenceTab = selectedComparisonTile?.key === 'difference';

    //react query to fetch 1st comparison incremental and promo tab data
    const {
        data: firstComparisonIncrementalData,
        loading: firstComparisonIncrementalDataLoading,
        error: firstComparisonIncrementalDataError,
        rawData: firstComparisonIncrementalRawData,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: { enabled: enabledVariable && !isGroupBy },
        queryKey: ['bump-report-comparison-1-incremental-promo', incrementalPromoVariables],
        lineChartQueryConfig: { enabled: enabledVariable && !isGroupBy },
        extras: {
            storeRawData: true,
            metric: selectedOptionsWithLabels,
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.incrementsPromoUrl,
        variables: {
            ...incrementalPromoVariables,
        },
    });

    //react query to fetch 2nd comparison incremental and promo tab data
    const {
        data: secondComparisonIncrementalData,
        loading: secondComparisonIncrementalDataLoading,
        error: secondComparisonIncrementalDataError,
        rawData: secondComparisonIncrementalRawData,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: { enabled: enabledVariable && !isGroupBy },
        queryKey: ['bump-report-comparison-2-incremental-promo', yaIncrementalPromoVariables],
        lineChartQueryConfig: { enabled: enabledVariable && !isGroupBy },
        extras: {
            storeRawData: true,
            metric: selectedOptionsWithLabels,
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.incrementsPromoUrl,
        variables: {
            ...yaIncrementalPromoVariables,
        },
    });

    //react query to fetch groupby 1st comparison tab data
    const {
        data: firstComparisonGroupByData,
        loading: firstComparisonGroupByLoading,
        error: firstComparisonGroupByError,
        rawData: firstComparisonGroupByRawData,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: { enabled: enabledVariable && isGroupBy },
        queryKey: ['bump-report-comparison-1-groupby', groupByVariables],
        lineChartQueryConfig: { enabled: false },
        extras: {
            storeRawData: true,
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.groupByUrl,
        variables: {
            ...groupByVariables,
        },
    });

    //react query to fetch groupby 2nd comparison tab data
    const {
        data: secondComparisonGroupByData,
        loading: secondComparisonGroupByLoading,
        error: secondComparisonGroupByError,
        rawData: secondComparisonGroupByRawData,
    } = useFetchBumpReportGraphRQ({
        method: 'post',
        appConfig,
        queryConfig: {
            enabled: enabledVariable && isGroupBy,
        },
        queryKey: ['bump-report-comparison-2-groupby', yaGroupByVariables],
        lineChartQueryConfig: { enabled: false },
        extras: {
            storeRawData: true,
            from: tileData.bumpReportTile.key,
            y2AxisLabel: selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
        },
        url: bumpReportUrls.groupByUrl,
        variables: {
            ...yaGroupByVariables,
        },
    });

    //react query to fetch incremental and promo tab export data
    const {
        data: basePromoExportData,
        error: basePromoExportDataError,
        loading: basePromoExportDataLoading,
        tableData: basePromoExportTableData,
        differenceData: basePromoExportDifferenceData,
    } = useFetchGraphExportComparison({
        method: 'post',
        appConfig,
        extras: { volTile: tileData.volTile, isDifference: isDifferenceTab },
        queryConfig: { enabled: enabledVariable && !isGroupBy },
        queryKey: ['bump-report-comparison-promo-export', incrementalPromoExportVariables],
        url: bumpReportUrls.incrementsPromoComparisonExportUrl,
        variables: {
            ...incrementalPromoExportVariables,
        },
    });

    //react query to fetch groupby tab export data
    const {
        data: groupByExportData,
        error: groupByExportDataError,
        loading: groupByExportDataLoading,
        tableData: groupByExportTableData,
        differenceData: groupByExportDifferenceData,
    } = useFetchGraphExportComparison({
        method: 'post',
        extras: { volTile: tileData.volTile, isDifference: isDifferenceTab },
        appConfig,
        queryConfig: { enabled: enabledVariable && isGroupBy },
        queryKey: ['bump-report-comparison-groupby-export', groupByExportVariables],
        url: bumpReportUrls.groupByComparisonExportUrl,
        variables: {
            ...groupByExportVariables,
        },
    });

    //tab mapping
    const tabChartColorsMapping: any = {
        increments: {
            data: {
                comparison: [firstComparisonIncrementalData, secondComparisonIncrementalData],
            },
            loading: {
                comparison: [firstComparisonIncrementalDataLoading, secondComparisonIncrementalDataLoading],
            },
            error: {
                comparison: [firstComparisonIncrementalDataError, secondComparisonIncrementalDataError],
            },
            legend: firstComparisonIncrementalData?.legend || [],
        },
        promo: {
            data: {
                comparison: [firstComparisonIncrementalData, secondComparisonIncrementalData],
            },
            loading: {
                comparison: [firstComparisonIncrementalDataLoading, secondComparisonIncrementalDataLoading],
            },
            error: {
                comparison: [firstComparisonIncrementalDataError, secondComparisonIncrementalDataError],
            },
            legend: firstComparisonIncrementalData?.legend || [],
        },
        groupBy: {
            data: {
                comparison: [firstComparisonGroupByData, secondComparisonGroupByData],
            },
            loading: {
                comparison: [firstComparisonGroupByLoading, secondComparisonGroupByLoading],
            },
            error: {
                comparison: [firstComparisonGroupByError, secondComparisonGroupByError],
            },
            legend: firstComparisonGroupByData?.legend || [],
        },
    };
    //chart data 1
    const chartData1 = useMemo(() => {
        const legend = tabChartColorsMapping[tileData.bumpReportTile.key].legend;
        const color = legend?.map((item: any) => item?.color);
        const data = tabChartColorsMapping[tileData.bumpReportTile.key].data.comparison?.[0];
        const loading = tabChartColorsMapping[tileData.bumpReportTile.key].loading.comparison?.[0];
        const error = tabChartColorsMapping[tileData.bumpReportTile.key].error.comparison?.[0];
        return { color, data, loading, error, tileData, legend };
    }, [
        tileData,
        selectedBumpReportTile1Data,
        selectedBumpReportTile2Data,
        firstComparisonIncrementalData,
        firstComparisonIncrementalDataError,
        firstComparisonGroupByError,
        firstComparisonIncrementalDataLoading,
        firstComparisonGroupByData,
        firstComparisonGroupByLoading,
    ]);

    //chart data 2
    const chartData2 = useMemo(() => {
        const legend = tabChartColorsMapping[tileData.bumpReportTile.key].legend;
        const color = legend?.map((item: any) => item?.color);
        const data = tabChartColorsMapping[tileData.bumpReportTile.key].data.comparison?.[1];
        const loading = tabChartColorsMapping[tileData.bumpReportTile.key].loading.comparison?.[1];
        const error = tabChartColorsMapping[tileData.bumpReportTile.key].error.comparison?.[1];
        return { color, data, loading, error, tileData, legend };
    }, [
        tileData,
        selectedBumpReportTile1Data,
        selectedBumpReportTile2Data,
        secondComparisonGroupByData,
        secondComparisonGroupByLoading,
        secondComparisonIncrementalDataLoading,
        secondComparisonIncrementalData,
        secondComparisonIncrementalDataError,
        secondComparisonGroupByError,
    ]);

    const differenceChartData = useMemo(() => {
        const enabledCondition = isGroupBy
            ? firstComparisonGroupByRawData && secondComparisonGroupByRawData
            : firstComparisonIncrementalRawData && secondComparisonIncrementalRawData;
        const currentRawData = isGroupBy
            ? [firstComparisonGroupByRawData, secondComparisonGroupByRawData]
            : [firstComparisonIncrementalRawData, secondComparisonIncrementalRawData];
        if (!enabledCondition) return { color: [], data: [], loading: false, error: null, tileData, legend: [] };
        // console.log(firstComparisonIncrementalRawData, 'logging dd');
        const differenceData = combineCyAndYa(currentRawData?.[0]?.barChartData, currentRawData?.[1]?.barChartData);
        const differenceLineData = isGroupBy
            ? null
            : currentRawData?.[0]?.lineChartData?.map((item: any, index: number) => {
                  return combineCyAndYa(item, currentRawData?.[1]?.lineChartData?.[index]);
              });
        const daysDifference = frequencyDaysMapping[frequencyToConsider?.key ?? 'W'] ?? 0;
        const parsedData = bumpReportChartParser(
            {
                y2Data: differenceLineData,
                barChartData: differenceData,
            },
            tileData.bumpReportTile.key as any,
            selectedOptionsWithLabels?.length === 1 ? selectedOptionsWithLabels?.[0]?.label : legendLabels,
            bumpReportConfig,
            appConfig,
            daysDifference,
            frequencyToConsider?.key,
        );
        const legend = tabChartColorsMapping[tileData.bumpReportTile.key].legend;
        const color = legend?.map((item: any) => item?.color);
        const data = parsedData;
        const loading = tabChartColorsMapping[tileData.bumpReportTile.key].loading.comparison?.[0];
        const error = tabChartColorsMapping[tileData.bumpReportTile.key].error.comparison?.[0];
        return { color, data, loading, error, tileData, legend };
    }, [
        firstComparisonIncrementalData,
        secondComparisonIncrementalData,
        firstComparisonGroupByData,
        secondComparisonGroupByData,
    ]);
    // console.log(differenceChartData, chartData1, 'logging data');
    const axisValues = useMemo(() => {
        let y1AxisLabel;
        //if tab is group by
        if (isGroupBy) {
            y1AxisLabel =
                groupByOptionSelected.label +
                ' ' +
                (tileData.volTile.key === 'vol' || tileData.volTile.key === 'units'
                    ? ''
                    : formatUnit(tileData.absTile.key, false, appConfig));
        } else {
            const y1AxisLabelMapping = mapAbsVolTile(tileData) || [];
            y1AxisLabel =
                y1AxisLabelMapping.length > 0
                    ? y1AxisLabelMapping[0] + ' ' + y1AxisLabelMapping[1]
                    : `Sales ${fetchCurrency(appConfig)}`;
        }

        return { y1AxisLabel, xAxisLabel };
    }, [tileData, groupByOptionSelected]);

    const handleSliderChange = (value: number) => {
        //mixpanel event

        if (value === 32) return setSliderValue(26);
        setSliderValue(value);
    };

    function findDifferenceInWeeks(date1: number, date2: number) {
        if (!date1 || !date2) return 0;
        const difference = dayjs.unix(date1).diff(dayjs.unix(date2), 'week');
        return difference + 1;
    }

    const timePeriodMapping = timePeriodSelection ? JSON.parse(timePeriodSelection) : {};

    const timeBucketValue = useMemo(() => {
        return findDifferenceInWeeks(timePeriodMapping?.['end_timestamp'], timePeriodMapping?.['start_timestamp']);
    }, [timePeriodSelection]);

    //used to debounce Loading state, so that UI wont flicker
    const { loading: combinedLoadingChart1 } = useDebounceLoading({
        loadingDependency:
            firstComparisonIncrementalDataLoading ||
            firstComparisonGroupByLoading ||
            timePeriodLoading ||
            allDimAndAttributeTypeList?.loading,
    });

    //used to debounce Loading state, so that UI wont flicker
    const { loading: combinedLoadingChart2 } = useDebounceLoading({
        loadingDependency:
            secondComparisonIncrementalDataLoading ||
            secondComparisonGroupByLoading ||
            timePeriodLoading ||
            allDimAndAttributeTypeList?.loading,
    });

    const exportLoading = isGroupBy ? groupByExportDataLoading : basePromoExportDataLoading;
    const exportError = isGroupBy ? groupByExportDataError : basePromoExportDataError;
    const exportData = isGroupBy
        ? isDifferenceTab
            ? groupByExportDifferenceData
            : groupByExportData
        : isDifferenceTab
        ? basePromoExportDifferenceData
        : basePromoExportData;
    const cyData = isDifferenceTab ? differenceChartData : chartData1;
    const yaData = chartData2;

    return (
        <section className="mt-4">
            <div className="flex justify-between items-center">
                <p className=" font-medium font-lg text-headerColor">
                    {isMedia ? 'Media' : 'Promo'} {promoComparisonSubHeader}
                </p>
                <TileButton
                    disabled={cyData?.loading || yaData?.loading}
                    data={comparisonTileData ?? []}
                    activeKey={selectedComparisonTile?.key}
                    onClick={(item: any) => {
                        setSelectedComparisonTile(item);
                    }}
                />
            </div>

            <div ref={refForGraph} className={`${commonClassName} bg-white mt-4`}>
                <div className="flex justify-between items-center ">
                    <p className="font-medium font-lg text-headerColor">{selectedComparisonTile?.label}</p>
                    <div className="flex items-center gap-2">
                        {!isGroupBy && (
                            <SelectionDropDown
                                className="dropdown-width-multi-select"
                                list={bumpChartY2AxisOptions}
                                checkedListObject={''}
                                disabled={combinedLoadingChart1 || combinedLoadingChart2}
                                setSelectedList={setOptionSelected}
                                selectedList={optionSelected || []}
                            />
                        )}
                        <CopyToClipBoard
                            // mixpanelEvent={() => trackEventMixpanel(BUMP_REPORT_CHART_COPY_CLICKED)}
                            refOfComponent={refForGraph}
                            removeScrollbar
                        />
                        {exportLoading ? (
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                        ) : (
                            !exportError && (
                                <DownloadExcel
                                    titleArray={isDifferenceTab ? undefined : [['CY']]}
                                    excelData={exportData?.data || []}
                                    headers={exportData?.header || []}
                                    title={`Comparison ${selectedComparisonTile?.label}`}
                                />
                            )
                        )}
                    </div>
                </div>

                {/* COMPARISON 1ST GRAPH AND DIFFERENCE TAB */}
                {cyData?.error && !combinedLoadingChart1 && !cyData?.data ? (
                    <ErrorBox msg={String(cyData?.error)} />
                ) : combinedLoadingChart1 ? (
                    <div style={{ height: 365, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Loader />
                    </div>
                ) : (
                    <div>
                        <div className="flex items-center justify-between my-2">
                            {/* Y1 AXIS */}
                            <p style={{ minWidth: legendWidth }} className={`${yAxisLabelClass} !top-0`}>
                                CY {axisValues.y1AxisLabel}
                            </p>
                            {/* LEGEND  */}
                            <div style={{ overflowX: 'auto' }}>
                                <Legend
                                    style={{
                                        justifyContent: 'initial',
                                        marginInline: 'auto',
                                        width: 'max-content',
                                        margin: 4,
                                        padding: 4,
                                        flex: 'none',
                                    }}
                                    data={cyData?.legend}
                                    showTooltip
                                />
                            </div>

                            {/* Y2 AXIS */}
                            <p
                                style={{ width: legendWidth }}
                                className={`${yAxisLabelClass} !top-0 text-right ${isGroupBy ? 'invisible' : ''}`}
                            >
                                {selectedOptionsWithLabels?.length === 1
                                    ? selectedOptionsWithLabels?.[0]?.label
                                    : multiSelectionDropdownLabel}
                            </p>
                        </div>

                        {/* CHART COMPONENT */}
                        <div style={{ height: bumpChartHeight }}>
                            <BumpChartComparisonContainer
                                chartContainers={[
                                    'bumpChart-comparison1-y1',
                                    COMPARISON_CHART_CONTAINER_IDS[0],
                                    'bumpChart-comparison1-y2',
                                ]}
                                showAllLabels={showAllLabels}
                                chartData={cyData}
                                tileData={tileData}
                                sliderValue={sliderValue}
                                chartConfig={chartConfig}
                                setChartConfig={setChartConfig}
                                optionSelected={selectedOptionsWithLabels}
                                timeBucketValue={timeBucketValue}
                                enableLineChart={!isGroupBy}
                                from={from}
                            />
                        </div>
                        <div className="flex justify-between items-center relative top-[4px] mb-[25px]">
                            <p className="w-[100px]"></p>
                            <p className="text-greyFont font-xs font-medium">CY {xAxisLabel}</p>
                            <p className="w-[100px]"></p>
                        </div>
                    </div>
                )}
                {/* COMPARISON 2ND GRAPH */}
                {selectedComparisonTile?.key !== 'difference' &&
                    // IF ERROR RETURN ERROR MESSAGE

                    (yaData.error && !combinedLoadingChart2 && !yaData.data ? (
                        <ErrorBox msg={String(yaData.error)} />
                    ) : combinedLoadingChart2 ? (
                        <div style={{ height: 365, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Loader />
                        </div>
                    ) : (
                        <div>
                            <div className="flex items-center justify-between my-2">
                                {/* Y1 AXIS */}
                                <p style={{ minWidth: legendWidth }} className={`${yAxisLabelClass} !top-0`}>
                                    YA {axisValues.y1AxisLabel}
                                </p>
                                {/* LEGEND  */}
                                <div style={{ overflowX: 'auto' }}></div>

                                {/* Y2 AXIS */}
                                <p
                                    style={{ width: legendWidth }}
                                    className={`${yAxisLabelClass} !top-0 text-right ${isGroupBy ? 'invisible' : ''}`}
                                >
                                    {selectedOptionsWithLabels?.length === 1
                                        ? selectedOptionsWithLabels?.[0]?.label
                                        : multiSelectionDropdownLabel}
                                </p>
                            </div>

                            {/* CHART COMPONENT */}
                            <div style={{ height: bumpChartHeight }}>
                                <BumpChartComparisonContainer
                                    chartContainers={[
                                        'bumpChart-comparison2-y1',
                                        COMPARISON_CHART_CONTAINER_IDS[1],
                                        'bumpChart-comparison2-y2',
                                    ]}
                                    showAllLabels={showAllLabels}
                                    chartData={yaData}
                                    tileData={tileData}
                                    sliderValue={sliderValue}
                                    chartConfig={chartConfig}
                                    setChartConfig={setChartConfig}
                                    optionSelected={selectedOptionsWithLabels}
                                    timeBucketValue={timeBucketValue}
                                    enableLineChart={!isGroupBy}
                                    from={from}
                                />
                            </div>
                            <div className="flex justify-between items-center relative top-[-25px] mb-[-25px]">
                                <p className="w-[100px]"></p>
                                <p className="text-greyFont font-xs font-medium">YA {xAxisLabel}</p>
                                <span id="bump-report-custom-slider" className="mt-4">
                                    <CustomSlider
                                        style={{ width: sliderWidth }}
                                        max={Number(sliderMax)}
                                        min={Number(sliderMin)}
                                        step={null}
                                        marks={sliderConfig}
                                        value={sliderValue}
                                        tooltip={{ open: false }}
                                        onChange={handleSliderChange}
                                    />
                                </span>
                            </div>
                        </div>
                    ))}
            </div>
            <div className="flex mt-4 justify-end items-center">
                <TileButton
                    disabled={exportLoading}
                    data={comparisonTableTileData}
                    activeKey={selectedTableComparisonTile?.key}
                    onClick={(item: any) => setSelectedTableComparisonTile(item)}
                />
            </div>
            <div className="my-4">
                <GrowthAnalysisTable
                    columns={
                        isGroupBy
                            ? groupByExportTableData?.[selectedTableComparisonTile?.key]?.columns
                            : basePromoExportTableData?.[selectedTableComparisonTile?.key]?.columns
                    }
                    data={
                        isGroupBy
                            ? groupByExportTableData?.[selectedTableComparisonTile?.key]?.rowsData
                            : basePromoExportTableData?.[selectedTableComparisonTile?.key]?.rowsData
                    }
                    tableLoading={exportLoading}
                />
            </div>
        </section>
    );
};

export default PromoComparisonContainerV2;

// Function to combine cy and ya arrays with difference calculations
const combineCyAndYa = (cy: any, ya: any) => {
    return {
        fields: cy?.fields, // Keep cy fields
        trends: cy?.trends?.map((cyTrend: any, index: number) => {
            const yaTrend = ya?.trends?.[index];

            // Calculate difference in values array
            const valuesDifference = cyTrend?.values?.map(
                (cyValue: number, valueIndex: number) =>
                    Number(cyValue ?? 0) - Number(yaTrend?.values?.[valueIndex] ?? 0),
            );

            // Calculate promo_lift difference
            const promoLiftDifference = Number(cyTrend?.promo_lift ?? 0) - Number(yaTrend?.promo_lift ?? 0);

            // Calculate timestamp differences as strings
            const startTimestampDiff = cyTrend?.start_timestamp;
            const endTimestampDiff = cyTrend?.end_timestamp;

            return {
                period_no: cyTrend?.period_no, // Keep cy field
                year_no: cyTrend?.year_no, // Keep cy field
                start_timestamp: startTimestampDiff,
                end_timestamp: endTimestampDiff,
                promo_lift: promoLiftDifference,
                values: valuesDifference,
            };
        }),
    };
};
